import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    InputAdornment,
    Divider,
    Tooltip,
    Pagination,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    IconButton,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CancelIcon from '@mui/icons-material/Cancel';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
    get_wallet_transactions,
    generate_coupon,
    get_coupon_list,
    cancel_coupon,
    delete_coupon,
    get_profile,
    get_coupon_invoice_download
} from '../../../api/ControllApi';
import { useSettings } from '../../../context/Settings';

const PurchaseCoupon = () => {
    const navigate = useNavigate();
    const { settings } = useSettings();
    const [amount, setAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('razorpay');
    const [loading, setLoading] = useState(false);
    const [balanceLoading, setBalanceLoading] = useState(true);
    const [walletTransactions, setWalletTransactions] = useState([]);
    const [successData, setSuccessData] = useState(null);
    const [userProfile, setUserProfile] = useState(null);

    const [coupons, setCoupons] = useState([]);
    const [couponLoading, setCouponLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedCouponId, setSelectedCouponId] = useState(null);

    useEffect(() => {
        fetchBalance();
        fetchProfile();
        fetchCoupons(1);
    }, []);

    const fetchBalance = async () => {
        try {
            setBalanceLoading(true);
            const res = await get_wallet_transactions();
            if (res && res.status) {
                setWalletTransactions(res.data || []);
            }
        } catch (err) {

        } finally {
            setBalanceLoading(false);
        }
    };

    const fetchProfile = async () => {
        try {
            const res = await get_profile();
            if (res && res.status) {
                setUserProfile(res.data);
            }
        } catch (err) {

        }
    };

    const fetchCoupons = async (pageNum) => {
        try {
            setCouponLoading(true);
            const res = await get_coupon_list(pageNum);
            if (res && res.status) {
                setCoupons(res.data.data || []);
                setTotalPages(res.data.last_page || 1);
            }
        } catch (err) {

            toast.error('Failed to load coupon list');
        } finally {
            setCouponLoading(false);
        }
    };

    const handleCancelCoupon = (id) => {
        setSelectedCouponId(id);
        setConfirmOpen(true);
    };

    const executeCancelCoupon = async () => {
        if (!selectedCouponId) return;
        setConfirmOpen(false);
        try {
            const res = await delete_coupon(selectedCouponId);
            if (res && res.status) {
                toast.success('Coupon cancelled successfully');
                fetchCoupons(page);
                fetchBalance();
            } else {
                toast.error(res?.message || 'Failed to cancel coupon');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error cancelling coupon');
        } finally {
            setSelectedCouponId(null);
        }
    };

    const handleShare = (coupon) => {
        const purchaseDate = coupon.created_at ? new Date(coupon.created_at).toLocaleDateString() : 'N/A';
        
        let expiresDateStr = coupon.expiry_date || coupon.expires_at;
        let expiresDateObj = null;
        if (expiresDateStr) {
            const normalizedDate = expiresDateStr.includes('T') ? expiresDateStr : expiresDateStr.replace(' ', 'T');
            expiresDateObj = new Date(normalizedDate);
        }
        if ((!expiresDateObj || isNaN(expiresDateObj.getTime())) && coupon.created_at) {
            expiresDateObj = new Date(coupon.created_at);
            expiresDateObj.setFullYear(expiresDateObj.getFullYear() + 1);
        }
        const expiryDate = (expiresDateObj && !isNaN(expiresDateObj.getTime()))
            ? expiresDateObj.toLocaleDateString()
            : 'N/A';

        const shareText = `🎉 Gift Voucher Alert! 🎉\n\nYou've received a gift voucher worth ₹${parseFloat(coupon.amount).toFixed(0)}!\n\n🔐 Voucher Code: ${coupon.name}\n\n📅 Purchase Date: ${purchaseDate}\n\n⏳ Expiry Date: ${expiryDate}\n\nEnjoy your gift! 🎁`;

        if (navigator.share) {
            navigator.share({
                title: 'Gift Voucher Alert!',
                text: shareText,
            }).catch(() => { });
        } else {
            navigator.clipboard.writeText(shareText);
            toast.success('Coupon details copied to clipboard!');
        }
    };


    const handleInvoiceDownload = async (id) => {
        try {
            const res = await get_coupon_invoice_download(id);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Invoice-${id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            toast.error("Failed to download invoice");
        }
    };

    const handleCopy = (code) => {
        navigator.clipboard.writeText(code);
        toast.success('Code copied to clipboard!');
    };

    const gstPercent = parseFloat(settings?.data?.coupon_service_charge || 0);
    const gstAmount = parseFloat(amount || 0) * gstPercent / 100;
    const totalWithGst = parseFloat(amount || 0) + gstAmount;

    const handlePurchase = async () => {
        const numAmount = Number(amount);
        if (!amount || isNaN(numAmount) || numAmount < 500 || numAmount > 50000) {
            toast.error('Amount must be between 500 and 50,000');
            return;
        }

        if (paymentMethod === 'wallet') {
            if (Number(userProfile?.wallet_balance || 0) < totalWithGst) {
                toast.error('Insufficient wallet balance');
                return;
            }
            submitPurchase({ wallet: true, amount: parseFloat(amount), gst: gstPercent });
        } else {
            initiateRazorpay();
        }
    };

    const initiateRazorpay = async () => {
        const loadRazorpay = () => {
            return new Promise((res) => {
                if (window.Razorpay) return res(window.Razorpay);
                const script = document.createElement("script");
                script.src = "https://checkout.razorpay.com/v1/checkout.js";
                script.onload = () => res(window.Razorpay);
                script.onerror = () => res(null);
                document.body.appendChild(script);
            });
        };

        const Razorpay = await loadRazorpay();
        if (!Razorpay) {
            toast.error('Razorpay SDK not loaded');
            return;
        }

        const options = {
            key: settings?.data?.payment_api_key || process.env.REACT_APP_RAZOR_PAY,
            amount: totalWithGst * 100,
            currency: "INR",
            name: "Cashback Farms",
            description: "Purchase Coupon",
            image: "/assets/icons/__Fav Icon.png",
            handler: async function (response) {
                const payload = {
                    amount: parseFloat(amount),
                    transaction_id: response.razorpay_payment_id,
                    payment_method: "razorpay",
                    gst: gstPercent,
                    user_gst: userProfile?.gst || ""
                };
                submitPurchase(payload);
            },
            prefill: {
                name: userProfile?.name || "",
                email: userProfile?.email || "",
                contact: userProfile?.phone || ""
            },
            theme: { color: "#82A240" },
        };

        const rzp = new Razorpay(options);
        rzp.open();
    };

    const submitPurchase = async (payload) => {
        setLoading(true);
        try {
            const res = await generate_coupon(payload);
            if (res && res.status) {
                toast.success(res.message || 'Coupon generated successfully!');
                setSuccessData(res.data);
                setAmount('');
                // ✅ After wallet purchase: refresh profile to update wallet balance
                if (payload.wallet) {
                    await fetchBalance();
                    await fetchProfile();
                }
                fetchCoupons(1);
            } else {
                toast.error(res?.message || 'Failed to generate coupon');
            }
        } catch (err) {

            toast.error(err.response?.data?.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    const renderCouponCard = (coupon) => {
        const isCancelled = coupon.cancel_status === 1;
        const isUsed = coupon.status === 1;

        const generatedDateObj = coupon.created_at ? new Date(coupon.created_at) : null;
        const generatedDate = generatedDateObj ? generatedDateObj.toLocaleDateString() : 'N/A';

        const expiresDateStr = coupon.expiry_date || coupon.expires_at;
        let expiresDateObj = null;

        if (expiresDateStr) {
            const normalizedDate = expiresDateStr.includes('T') ? expiresDateStr : expiresDateStr.replace(' ', 'T');
            expiresDateObj = new Date(normalizedDate);
            if (isNaN(expiresDateObj.getTime())) expiresDateObj = null;
        }

        if (!expiresDateObj && generatedDateObj) {
            expiresDateObj = new Date(generatedDateObj);
            expiresDateObj.setFullYear(expiresDateObj.getFullYear() + 1);
        }

        const expiresDate = (expiresDateObj && !isNaN(expiresDateObj.getTime()))
            ? expiresDateObj.toLocaleDateString()
            : 'N/A';

        const isExpired = expiresDateObj && new Date() > expiresDateObj;
        const isInactive = isCancelled || isExpired;

        const statusLabel = isCancelled ? 'Cancelled' : isExpired ? 'Expired' : isUsed ? 'Used' : 'Active';
        const statusClass = isCancelled || isExpired ? 'bg-danger' : isUsed ? 'bg-secondary' : 'bg-success';

        return (
            <div className="col-12 col-sm-6 col-md-4 mb-3" key={coupon.id}>
                <div
                    id={`coupon-card-${coupon.id}`}
                    style={{
                        background: '#ffffff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                        overflow: 'hidden',
                        position: 'relative',
                        height: '100%',
                        opacity: isInactive ? 0.75 : 1,
                    }}
                >
                    <div className="p-3">
                        {/* Amount + Status */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#82A240' }}>
                                ₹{parseFloat(coupon.amount).toFixed(0)}
                            </span>
                            <span className={`badge ${statusClass} text-white`} style={{ fontSize: '0.75rem' }}>
                                {statusLabel}
                            </span>
                        </div>

                        {/* Coupon Code */}
                        <div
                            className="d-flex align-items-center justify-content-between mb-2 px-2 py-1"
                            style={{ background: '#f5f5f5', borderRadius: '8px', border: '1px dashed #bdbdbd' }}
                        >
                            <span style={{ fontWeight: 600, letterSpacing: '1px', fontSize: '0.95rem', color: '#333' }}>
                                {coupon.name}
                            </span>
                            <button
                                className="btn btn-link p-0 ms-2"
                                style={{ color: '#82A240' }}
                                onClick={() => handleCopy(coupon.name)}
                                title="Copy Code"
                            >
                                <ContentCopyIcon fontSize="small" />
                            </button>
                        </div>

                        {/* Dates */}
                        <div className="mb-2">
                            <div style={{ fontSize: '0.78rem', color: '#757575' }}>
                                Purchased: {generatedDate}
                            </div>
                            <div style={{ fontSize: '0.78rem', color: isExpired ? '#d32f2f' : '#757575', fontWeight: isExpired ? 700 : 400 }}>
                                Expires on: {expiresDate}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        {!isInactive && (
                            <div className="d-flex justify-content-center gap-4 pt-1 border-top mt-2">
                                <Tooltip title="Download Invoice">
                                    <button className="btn btn-link p-1" style={{ color: '#82A240' }} onClick={() => handleInvoiceDownload(coupon.id)}>
                                        <FileDownloadIcon fontSize="small" />
                                    </button>
                                </Tooltip>
                                <Tooltip title="Share">
                                    <button className="btn btn-link p-1" style={{ color: '#82A240' }} onClick={() => handleShare(coupon)}>
                                        <ShareIcon fontSize="small" />
                                    </button>
                                </Tooltip>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="container py-4">
            <div className="row justify-content-center">
                <div className="col-12 col-lg-9">

                    {/* Page Title */}
                    <h4 className="fw-bold mb-4" style={{ color: '#82A240' }}>Gift/Payment Coupon</h4>

                    {/* Payment Method Toggle */}
                    <div className="d-flex gap-3 mb-4">
                        <button
                            className={`btn d-flex align-items-center gap-2 px-4 py-2 fw-semibold`}
                            style={{
                                borderRadius: '10px',
                                backgroundColor: paymentMethod === 'razorpay' ? '#82A240' : 'transparent',
                                color: paymentMethod === 'razorpay' ? '#fff' : '#82A240',
                                border: '1px solid #82A240'
                            }}
                            onClick={() => setPaymentMethod('razorpay')}
                        >
                            <CreditCardIcon fontSize="small" /> Razorpay
                        </button>
                        <button
                            className={`btn d-flex align-items-center gap-2 px-4 py-2 fw-semibold`}
                            style={{
                                borderRadius: '10px',
                                backgroundColor: paymentMethod === 'wallet' ? '#82A240' : 'transparent',
                                color: paymentMethod === 'wallet' ? '#fff' : '#82A240',
                                border: '1px solid #82A240'
                            }}
                            onClick={() => setPaymentMethod('wallet')}
                        >
                            <AccountBalanceWalletIcon fontSize="small" /> Wallet
                        </button>
                    </div>

                    {/* Wallet Balance Display */}
                    {paymentMethod === 'wallet' && (
                        <div className="alert d-flex justify-content-between align-items-center mb-3" style={{ background: '#f1f8e9', border: '1px solid #82A240', borderRadius: '10px' }}>
                            <span className="fw-semibold" style={{ color: '#82A240' }}>Your Wallet Balance</span>
                            <span className="fw-bold fs-5" style={{ color: '#82A240' }}>
                                {userProfile
                                    ? `₹${Number(userProfile.wallet_balance || 0).toFixed(2)}`
                                    : <CircularProgress size={16} color="inherit" />
                                }
                            </span>
                        </div>
                    )}

                    {/* Amount Input */}
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Enter Coupon Amount</label>
                        <div className="input-group">
                            <span className="input-group-text bg-white fw-bold">₹</span>
                            <input
                                type="number"
                                className="form-control"
                                placeholder="Enter the coupon amount"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                min="500"
                                max="50000"
                                style={{ borderRadius: '0 10px 10px 0' }}
                            />
                        </div>
                        <small className="text-muted mt-1 d-block">
                            Limit: ₹500 to ₹50,000
                        </small>
                    </div>

                    {/* Service Tax Breakdown */}
                    {amount > 0 && (
                        <div className="mb-3 p-3" style={{ background: '#f9f9f9', borderRadius: '10px', border: '1px solid #e0e0e0' }}>
                            <div className="d-flex justify-content-between mb-1">
                                <span className="text-muted small">Amount</span>
                                <span className="fw-semibold small">₹{parseFloat(amount).toLocaleString()}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-1">
                                <span className="text-muted small">Service Tax ({gstPercent}%)</span>
                                <span className="fw-semibold small text-danger">+ ₹{gstAmount.toLocaleString()}</span>
                            </div>
                            <hr className="my-2" />
                            <div className="d-flex justify-content-between">
                                <span className="fw-bold" style={{ color: '#82A240' }}>Total Payable</span>
                                <span className="fw-bold" style={{ color: '#82A240' }}>₹{totalWithGst.toLocaleString()}</span>
                            </div>
                        </div>
                    )}

                    {/* Insufficient Balance Warning */}
                    {paymentMethod === 'wallet' && totalWithGst > Number(userProfile?.wallet_balance || 0) && (
                        <div className="alert alert-warning mb-3" style={{ borderRadius: '10px' }}>
                            Insufficient balance. You need ₹{totalWithGst.toFixed(2)} but have ₹{Number(userProfile?.wallet_balance || 0).toFixed(2)}.
                        </div>
                    )}

                    {/* Purchase Button */}
                    <button
                        className="btn w-100 fw-bold py-2 mb-5"
                        style={{ borderRadius: '10px', fontSize: '1rem', backgroundColor: '#82A240', color: '#fff', borderColor: '#82A240' }}
                        onClick={handlePurchase}
                        disabled={loading || !amount || (paymentMethod === 'wallet' && totalWithGst > Number(userProfile?.wallet_balance || 0))}
                    >
                        {loading
                            ? <CircularProgress size={20} color="inherit" />
                            : `Gift Coupon (₹${amount || 0})`
                        }
                    </button>

                    <hr className="my-4" />

                    {/* My Coupons Header */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-bold mb-0" style={{ color: '#82A240' }}>My Coupons</h5>
                        <button className="btn btn-link p-0" onClick={() => fetchCoupons(1)}>
                            {couponLoading
                                ? <CircularProgress size={16} />
                                : <CheckCircleOutlineIcon fontSize="small" style={{ color: '#82A240' }} />
                            }
                        </button>
                    </div>

                    {/* Coupon List */}
                    {couponLoading && coupons.length === 0 ? (
                        <div className="text-center py-4">
                            <CircularProgress style={{ color: '#82A240' }} />
                        </div>
                    ) : (
                        <>
                            <div style={{ maxHeight: '500px', overflowY: 'auto', paddingRight: '4px' }}>
                                <div className="row">
                                    {coupons.length > 0
                                        ? coupons.map(renderCouponCard)
                                        : (
                                            <div className="col-12">
                                                <div className="alert alert-info" style={{ borderRadius: '10px' }}>
                                                    No coupons found.
                                                </div>
                                            </div>
                                        )
                                    }
                                </div>
                            </div>

                            {totalPages > 1 && (
                                <div className="d-flex justify-content-center mt-4">
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={(e, v) => { setPage(v); fetchCoupons(v); }}
                                        color="success"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Cancel Confirmation Dialog */}
            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, color: '#82A240' }}>
                    Cancel Coupon
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: '18px', color: 'text.primary' }}>
                        Do you want to cancel this coupon?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 600 }}
                    >
                        No
                    </Button>
                    <Button
                        onClick={executeCancelCoupon}
                        variant="contained"
                        color="error"
                        sx={{ borderRadius: 2, textTransform: 'none', px: 3, fontWeight: 600 }}
                    >
                        Yes, Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default PurchaseCoupon;